import React, { useEffect, useRef, useState } from "react";
import { Redirect, useParams } from "react-router-dom";
import { useDocumentMeta, useI18n } from "../i18n";
import { formatPostDate, getPost } from "./posts";
import { extractHeadings } from "./headings";
import Markdown from "./Markdown";

const TOC_OFFSET = 120;

function nearPageBottom() {
  const root = document.scrollingElement || document.documentElement;
  return root.scrollHeight - root.scrollTop - root.clientHeight <= 8;
}

function activeHeadingId(ids) {
  if (!ids.length) return "";
  if (nearPageBottom()) return ids[ids.length - 1];
  let current = ids[0];
  ids.forEach((id) => {
    const target = document.getElementById(id);
    if (target && target.getBoundingClientRect().top <= TOC_OFFSET) {
      current = id;
    }
  });
  return current;
}

export default function BlogPost() {
  const { slug } = useParams();
  const { t, locale } = useI18n();
  const post = getPost(slug, locale);
  const headings = post ? extractHeadings(post.markdown) : [];
  const headingIds = headings.map((item) => item.id).join("\n");
  const [activeId, setActiveId] = useState("");
  const tocRef = useRef(null);
  const tocLinks = useRef({});

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  useDocumentMeta(
    post
      ? {
          title: `${post.title} — NewFW`,
          description: post.excerpt,
          path: `/blog/${post.slug}`,
        }
      : {}
  );

  useEffect(() => {
    const ids = headingIds ? headingIds.split("\n") : [];
    if (!ids.length) {
      setActiveId("");
      return undefined;
    }
    let frame = 0;
    const update = () => {
      const next = activeHeadingId(ids);
      setActiveId((prev) => (prev === next ? prev : next));
    };
    const onScroll = () => {
      if (frame) return;
      frame = window.requestAnimationFrame(() => {
        frame = 0;
        update();
      });
    };
    update();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    const content = document.querySelector(".nfw-post-content");
    const resize = content ? new ResizeObserver(onScroll) : null;
    if (resize && content) resize.observe(content);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      if (resize) resize.disconnect();
      if (frame) window.cancelAnimationFrame(frame);
    };
  }, [headingIds]);

  useEffect(() => {
    const nav = tocRef.current;
    const link = tocLinks.current[activeId];
    if (!nav || !link) return;
    const navRect = nav.getBoundingClientRect();
    const linkRect = link.getBoundingClientRect();
    if (linkRect.top < navRect.top + 8) {
      nav.scrollTop -= navRect.top + 8 - linkRect.top;
    } else if (linkRect.bottom > navRect.bottom - 8) {
      nav.scrollTop += linkRect.bottom - (navRect.bottom - 8);
    }
  }, [activeId]);

  const jumpTo = (event, id) => {
    event.preventDefault();
    setActiveId(id);
    const target = document.getElementById(id);
    if (target) target.scrollIntoView({ behavior: "smooth", block: "start" });
    window.history.replaceState(null, "", `${window.location.pathname}#${id}`);
  };

  if (!post) return <Redirect to="/blog" />;

  return (
    <main className="nfw-post-page">
      <article className="nfw-post">
        <header className="nfw-post-hero">
          <div className="nfw-wrap">
            <div className="nfw-blog-meta">
              <time dateTime={post.date}>
                {formatPostDate(post.date, locale)}
              </time>
              {post.tags.map((tag) => (
                <span className="nfw-tag" key={tag}>
                  {tag}
                </span>
              ))}
            </div>
            <h1>{post.title}</h1>
            <p className="nfw-post-excerpt">{post.excerpt}</p>
          </div>
        </header>
        <div className="nfw-post-body">
          <div className="nfw-wrap nfw-post-layout">
            <aside className="nfw-post-toc" ref={tocRef}>
              <p className="nfw-kicker">{t.blog.toc}</p>
              <nav>
                {headings.map((item) => (
                  <a
                    key={item.id}
                    ref={(node) => {
                      tocLinks.current[item.id] = node;
                    }}
                    href={`#${item.id}`}
                    onClick={(event) => jumpTo(event, item.id)}
                    aria-current={activeId === item.id ? "location" : undefined}
                    className={[
                      item.level > 2 ? "nfw-post-toc-h3" : "nfw-post-toc-h2",
                      activeId === item.id ? "active" : "",
                    ]
                      .filter(Boolean)
                      .join(" ")}
                  >
                    {item.text}
                  </a>
                ))}
              </nav>
            </aside>
            <div className="nfw-post-content">
              <Markdown source={post.markdown} />
            </div>
          </div>
        </div>
      </article>
    </main>
  );
}
